import { realWorldPictureSize } from "@brickme/project-core/src/model/picture.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import type { Scene } from "@brickme/project-core/src/scenes/types.ts";

type DrawSizeConfiguration = {
	readonly minTotalMargin: number;
};

/**
 * Rendering on to canvas' where bricks aren't exact pixel sizes gives weird visual
 * effects. This helper finds the largest draw size possible for the available area
 * which keeps integer pixels for brick sizes.
 */
function createDrawSize(
	brickedDimensions: GenericSize<number>,
	{ width: parentWidth, height: parentHeight }: GenericSize<number>,
	scene: Scene | undefined,
	{ minTotalMargin }: DrawSizeConfiguration,
) {
	if (brickedDimensions.width === 0 || brickedDimensions.height === 0) {
		return { width: 0, height: 0 };
	}

	if (scene !== undefined) {
		const sceneWidth = scene.fullSize.realWorldSize.width.mm;
		const sceneHeight = scene.fullSize.realWorldSize.height.mm;
		const availableWidth = Math.max(0, parentWidth - minTotalMargin);
		const availableHeight = Math.max(0, parentHeight - minTotalMargin);
		const xScaleToContain = availableWidth / sceneWidth;
		const yScaleToContain = availableHeight / sceneHeight;
		const useScaleToContain = Math.min(xScaleToContain, yScaleToContain);
		const containedWidth = Math.floor(useScaleToContain * sceneWidth);
		const containedHeight = Math.floor(useScaleToContain * sceneHeight);
		return {
			width: containedWidth,
			height: containedHeight,
		};
	}

	const worldSize = realWorldPictureSize(brickedDimensions);
	const availableWidth = Math.max(0, parentWidth - minTotalMargin);
	const availableHeight = Math.max(0, parentHeight - minTotalMargin);
	const xScaleToContain = availableWidth / worldSize.width.mm;
	const yScaleToContain = availableHeight / worldSize.height.mm;
	const useScaleToContain = Math.min(xScaleToContain, yScaleToContain);
	const containedWidth = Math.floor(useScaleToContain * worldSize.width.mm);
	// Note: Unsure about brickSize here - seems to count frame dimensions
	const brickSize = Math.floor(containedWidth / brickedDimensions.width);

	// Bricks are sub pixel size, so take up as much as we can while keeping
	// square bricks. pixelsUseScale is the (sub-pixel) size of one brick.
	if (brickSize === 0) {
		const pixelsXScale = availableWidth / brickedDimensions.width;
		const pixelsYScale = availableHeight / brickedDimensions.height;
		const pixelsUseScale = Math.min(pixelsXScale, pixelsYScale);
		return {
			width: pixelsUseScale * brickedDimensions.width,
			height: pixelsUseScale * brickedDimensions.height,
		};
	}

	const canvasWidth = brickSize * brickedDimensions.width;
	const canvasHeight = brickSize * brickedDimensions.height;
	return { width: canvasWidth, height: canvasHeight };
}

export { createDrawSize };
